/**
 * Servicio centralizado de notificaciones para DataFast
 * Maneja SweetAlert2 y Toast de forma consistente
 */

import { getFriendlyErrorMessage } from '@/utils/datafastTokenManager'

/**
 * Muestra un error con SweetAlert2
 * @param {Object} context - Contexto Vue (this)
 * @param {String} message - Mensaje de error
 * @param {String} title - Título del modal
 * @param {String} errorCode - Código de error opcional para mensaje amigable
 */
export function showDataFastError(
    context,
    message,
    title = 'Error',
    errorCode = null
) {
    const friendlyMessage = errorCode
        ? getFriendlyErrorMessage(errorCode)
        : message

    if (context.$swal) {
        context.$swal({
            title,
            text: friendlyMessage,
            icon: 'error',
            customClass: {
                confirmButton: 'btn btn-danger',
            },
            buttonsStyling: false,
        })
    }
}

/**
 * Muestra un mensaje de éxito con SweetAlert2
 * @param {Object} context - Contexto Vue (this)
 * @param {String} message - Mensaje de éxito
 * @param {String} title - Título del modal
 */
export function showDataFastSuccess(context, message, title = 'Éxito') {
    if (context.$swal) {
        context.$swal({
            title,
            text: message,
            icon: 'success',
            customClass: {
                confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
        })
    }
}

/**
 * Muestra una advertencia con SweetAlert2
 * @param {Object} context - Contexto Vue (this)
 * @param {String} message - Mensaje de advertencia
 * @param {String} title - Título del modal
 */
export function showDataFastWarning(context, message, title = 'Advertencia') {
    if (context.$swal) {
        context.$swal({
            title,
            text: message,
            icon: 'warning',
            customClass: {
                confirmButton: 'btn btn-warning',
            },
            buttonsStyling: false,
        })
    }
}

/**
 * Muestra un toast de éxito
 * @param {Object} context - Contexto Vue (this)
 * @param {String} message - Mensaje
 * @param {String} title - Título del toast
 */
export function showDataFastToastSuccess(
    context,
    title = 'Éxito',
    message = ''
) {
    if (context.$toast) {
        context.$toast({
            component: context.$options.components.ToastificationContent || {},
            props: {
                title,
                text: message,
                variant: 'success',
                icon: 'CheckIcon',
            },
        })
    }
}

/**
 * Muestra un toast de error
 * @param {Object} context - Contexto Vue (this)
 * @param {String} message - Mensaje
 * @param {String} title - Título del toast
 */
export function showDataFastToastError(context, title = 'Error', message = '') {
    if (context.$toast) {
        context.$toast({
            component: context.$options.components.ToastificationContent || {},
            props: {
                title,
                text: message,
                variant: 'danger',
                icon: 'XIcon',
            },
        })
    }
}

/**
 * Muestra resultado de pago (éxito o rechazo)
 * @param {Object} context - Contexto Vue (this)
 * @param {Boolean} success - Si fue exitoso
 * @param {String} message - Mensaje
 * @param {String} code - Código de respuesta
 * @param {Object} subscription - Datos de suscripción opcional
 */
export function showPaymentResult(
    context,
    success,
    message,
    code = null,
    subscription = null
) {
    let alertText = message

    if (success && subscription && subscription.created) {
        alertText += '\n\nTu tarjeta ha sido guardada para pagos futuros.'
    }

    const friendlyMessage = code ? getFriendlyErrorMessage(code) : alertText

    if (context.$swal) {
        if (success) {
            context.$swal({
                title: 'Luki agradece tu pago!',
                html: 'En un momento tu pago sera procesado.<br><strong>Tu reconexion se realizara en 5 minutos.</strong>',
                icon: 'success',
                customClass: {
                    confirmButton: 'btn btn-success',
                },
                buttonsStyling: false,
            })
        } else {
            context.$swal({
                title: 'Pago Rechazado',
                text: friendlyMessage,
                icon: 'error',
                customClass: {
                    confirmButton: 'btn btn-danger',
                },
                buttonsStyling: false,
            })
        }
    }
}

/**
 * Muestra resultado de tokenización (éxito o rechazo)
 * @param {Object} context - Contexto Vue (this)
 * @param {Boolean} success - Si fue exitoso
 * @param {String} message - Mensaje
 * @param {String} code - Código de respuesta
 */
export function showTokenizationResult(context, success, message, code = null) {
    const friendlyMessage = code ? getFriendlyErrorMessage(code) : message

    if (context.$swal) {
        context.$swal({
            title: success ? 'Tarjeta Registrada' : 'Registro Rechazado',
            text: friendlyMessage,
            icon: success ? 'success' : 'error',
            customClass: {
                confirmButton: `btn btn-${success ? 'success' : 'danger'}`,
            },
            buttonsStyling: false,
        })
    }
}

/**
 * Muestra confirmación de eliminación
 * @param {Object} context - Contexto Vue (this)
 * @param {String} cardLastDigits - Últimos dígitos de la tarjeta
 * @returns {Promise<Boolean>} - true si el usuario confirmó
 */
export async function confirmCardDeletion(context, cardLastDigits) {
    if (!context.$swal) return false

    const result = await context.$swal({
        title: '¿Eliminar tarjeta?',
        text: `Tarjeta terminada en ${cardLastDigits}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary ml-1',
        },
        buttonsStyling: false,
    })

    return result.isConfirmed
}

export default {
    showDataFastError,
    showDataFastSuccess,
    showDataFastWarning,
    showDataFastToastSuccess,
    showDataFastToastError,
    showPaymentResult,
    showTokenizationResult,
    confirmCardDeletion,
}
